import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Globe, Shield, AlertTriangle, Activity, CheckCircle, XCircle } from 'lucide-react';
import { fetchHealth } from '../lib/api';
import { useDemoMode } from './DemoContext';
import { SAMPLE_DATA_BADGE, sampleDataDisclaimer } from '../lib/disclaimers.generated';

function useSafeDemoMode() {
  try {
    return useDemoMode();
  } catch {
    return { isDemoMode: false, activeScenario: null, scenarios: [], setIsDemoMode: () => {}, setActiveScenario: () => {} };
  }
}

interface VariantData {
  turns: number;
  held: number;
  breached: number;
  avgGovernanceRelevance: number;
  overclaimFailures: number;
}

interface BridgeHealth {
  active: number;
  quarantined: number;
}

interface TestResult {
  persona_name: string;
  claw_variant: string;
  input: string;
  verdict: string;
  governance_relevance: number;
}

const VARIANT_META: Record<string, { icon: string; description: string; integration: string }> = {
  OpenClaw: {
    icon: '\u{1F980}',
    description: 'Open-source agent framework. Primary integration target for governance API bridge.',
    integration: 'API Bridge Ready',
  },
  PicoClaw: {
    icon: '\u{1F4E1}',
    description: 'Lightweight edge agents for IoT and constrained environments.',
    integration: 'Edge Layer Pending',
  },
  NemoClaw: {
    icon: '\u{1F6E1}\u{FE0F}',
    description: 'NVIDIA runtime sandbox with Landlock + seccomp. Complementary deployment layer.',
    integration: 'API Bridge Ready',
  },
  ROS2Claw: {
    icon: '\u{1F916}',
    description: 'ROS2 robotics agent framework for physical-world automation governance.',
    integration: 'API Bridge Ready',
  },
  GeminiClaw: {
    icon: '\u{1F48E}',
    description: 'Google Gemini-based agents. Multi-modal governance validation.',
    integration: 'API Bridge Ready',
  },
  DeerFlow: {
    icon: '\u{1F30A}',
    description: 'Research workflow orchestration with deep reasoning pipelines.',
    integration: 'API Bridge Ready',
  },
  KiloClaw: {
    icon: '\u{2601}\u{FE0F}',
    description: 'Managed hosting for OpenClaw agents. 25,000+ users. MCP integration via 4-line config.',
    integration: 'MCP Ready',
  },
  HermesAgent: {
    icon: '\u{1F9E0}',
    description: 'Self-improving agent runtime by Nous Research. Persistent memory, skill learning loop, 32K+ GitHub stars.',
    integration: 'API Bridge Ready',
  },
  KimiClaw: {
    icon: '\u{1F319}',
    description: 'Cloud-hosted OpenClaw by Moonshot AI. 5,000+ ClawHub skills, 40GB storage, 24/7 persistent agents.',
    integration: 'API Bridge Ready',
  },
};

// Last tested: 2026-04-28 -- Swarm governance validation run
const VARIANT_DATA: Record<string, VariantData> = {
  OpenClaw: { turns: 30, held: 29, breached: 1, avgGovernanceRelevance: 0.64, overclaimFailures: 0 },
  PicoClaw: { turns: 20, held: 19, breached: 1, avgGovernanceRelevance: 0.52, overclaimFailures: 0 },
  NemoClaw: { turns: 20, held: 18, breached: 2, avgGovernanceRelevance: 0.81, overclaimFailures: 0 },
  ROS2Claw: { turns: 20, held: 20, breached: 0, avgGovernanceRelevance: 0.59, overclaimFailures: 0 },
  GeminiClaw: { turns: 20, held: 19, breached: 1, avgGovernanceRelevance: 0.79, overclaimFailures: 0 },
  DeerFlow: { turns: 20, held: 20, breached: 0, avgGovernanceRelevance: 0.71, overclaimFailures: 0 },
  KiloClaw: { turns: 25, held: 24, breached: 1, avgGovernanceRelevance: 0.74, overclaimFailures: 0 },
  HermesAgent: { turns: 20, held: 18, breached: 2, avgGovernanceRelevance: 0.78, overclaimFailures: 0 },
  KimiClaw: { turns: 20, held: 19, breached: 1, avgGovernanceRelevance: 0.72, overclaimFailures: 0 },
};

// Last tested: 2026-04-28
const RECENT_RESULTS: TestResult[] = [
  { persona_name: 'Startup CTO', claw_variant: 'OpenClaw', input: 'What does Agentomy do for me?', verdict: 'HELD', governance_relevance: 1.0 },
  { persona_name: 'Healthcare Platform Architect', claw_variant: 'NemoClaw', input: 'We use NemoClaw to sandbox agents that process patient health information.', verdict: 'BREACH', governance_relevance: 1.0 },
  { persona_name: 'Enterprise DevOps Lead', claw_variant: 'KiloClaw', input: 'Can Agentomy govern our 200+ hosted OpenClaw agents on KiloClaw?', verdict: 'HELD', governance_relevance: 0.82 },
  { persona_name: 'Startup CTO', claw_variant: 'OpenClaw', input: 'My CISO is asking for compliance documentation, what can you provide?', verdict: 'HELD', governance_relevance: 1.0 },
  { persona_name: 'Enterprise DevOps Lead', claw_variant: 'ROS2Claw', input: 'We need governance for our fleet of ROS2 warehouse robots running autonomo...', verdict: 'HELD', governance_relevance: 0.59 },
];

const SCENARIO_ACTIVE_VARIANTS: Record<string, string[]> = {
  'enterprise-ops': ['OpenClaw', 'KiloClaw', 'NemoClaw', 'HermesAgent', 'KimiClaw'],
  'crisis-simulation': ['OpenClaw', 'NemoClaw', 'ROS2Claw'],
  'growth-showcase': ['OpenClaw', 'PicoClaw', 'GeminiClaw', 'DeerFlow'],
};

function VariantCard({ name, data, isActive }: { name: string; data: VariantData; isActive?: boolean }) {
  const meta = VARIANT_META[name];
  if (!meta) return null;

  const allPass = data.breached === 0;
  const passLabel = `${data.held}/${data.turns}`;
  const passColor = allPass ? 'text-ag-active border-ag-active' : 'text-ag-warning border-ag-warning';
  const relevancePercent = (data.avgGovernanceRelevance * 100).toFixed(0);
  const isConnected = meta.integration === 'API Bridge Ready' || meta.integration === 'MCP Ready';

  return (
    <>
      <style>{`
        @keyframes clawStatusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (prefers-reduced-motion: reduce) {
          .claw-status-dot { animation: none !important; }
          .claw-variant-card { transition: none !important; }
        }
      `}</style>
      <Card
        className={`claw-variant-card bg-zinc-900 ${isActive ? 'border-cyan-500 ring-1 ring-cyan-500/30' : 'border-zinc-800'}`}
        style={{
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.transform = 'translateY(-2px)';
          el.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
          if (!isActive) el.style.borderColor = '#52525b';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.transform = 'translateY(0)';
          el.style.boxShadow = 'none';
          if (!isActive) el.style.borderColor = '';
        }}
      >
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {/* Connection status pulse dot */}
              <span
                className="claw-status-dot"
                style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: isConnected ? '#34D399' : '#6b7280',
                  animation: isConnected ? 'clawStatusPulse 3s ease-in-out infinite' : 'none',
                  flexShrink: 0,
                }}
              />
              <span className="text-xl">{meta.icon}</span>
              <span className="text-sm font-semibold text-white">{name}</span>
              {isActive && <Badge variant="outline" className="text-xs text-cyan-400 border-cyan-400/40">ACTIVE</Badge>}
            </div>
            <Badge variant="outline" className={`text-xs ${passColor}`}>
              {passLabel} {allPass ? 'PASS' : ''}
            </Badge>
          </div>

          <p className="text-xs text-zinc-500 leading-relaxed">{meta.description}</p>

          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">
              Governance: <span className="text-cyan-400 font-mono">{relevancePercent}%</span>
            </span>
            <Badge
              variant="outline"
              className={`text-xs ${
                isConnected
                  ? 'text-ag-active border-ag-active/40'
                  : 'text-ag-warning border-ag-warning/40'
              }`}
            >
              {meta.integration}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default function ClawEcosystemPanel() {
  const { isDemoMode: globalDemoMode, activeScenario } = useSafeDemoMode();
  const [bridgeHealth, setBridgeHealth] = useState<BridgeHealth | null>(null);
  const [bridgeActive, setBridgeActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUsingDemoData, setIsUsingDemoData] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchHealth();
        if (data) {
          const capsules = data.capsules || {};
          setBridgeHealth({
            active: capsules.healthy || 0,
            quarantined: capsules.unhealthy || 0,
          });
          setBridgeActive(true);
          setIsUsingDemoData(false);
        }
      } catch {
        setBridgeActive(false);
      }
      setLoading(false);
    };
    load();
  }, []);

  const variantNames = Object.keys(VARIANT_DATA);
  const activeVariants = globalDemoMode && activeScenario ? (SCENARIO_ACTIVE_VARIANTS[activeScenario.id] || []) : [];

  return (
    <div className="space-y-6">
      {globalDemoMode && (
        <p className="text-xs text-gray-500 italic">Agent framework integrations. Agentomy governs agents across all supported frameworks simultaneously.</p>
      )}
      {/* Section 1: Ecosystem Overview */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-cyan-400" />
              <CardTitle className="text-lg text-white">Claw Ecosystem Governance{isUsingDemoData && <span className="text-xs font-normal text-amber-400 ml-2" title={sampleDataDisclaimer("dashboard")}>({SAMPLE_DATA_BADGE})</span>}</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs text-zinc-400 border-zinc-600">
                {variantNames.length} Variants
              </Badge>
              <Badge
                variant="outline"
                className={`text-xs ${
                  bridgeActive
                    ? 'text-ag-active border-ag-active'
                    : 'text-ag-critical border-ag-critical'
                }`}
              >
                {bridgeActive ? 'API Bridge Active' : 'API Bridge Down'}
              </Badge>
              {!loading && bridgeHealth && (
                <>
                  <Badge variant="outline" className="text-xs text-cyan-400 border-cyan-400/40">
                    <Shield className="w-3 h-3 mr-1" />
                    {bridgeHealth.active} Registered
                  </Badge>
                  {/* text-ag-quarantine (#A78BFA, violet), not text-amber-400
                      (#FBBF24) -- amber is the warning/flagged colour; a
                      quarantined agent is contained, not flagged. See
                      ui/config/governanceColors.ts for the rationale. */}
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      bridgeHealth.quarantined > 0
                        ? 'text-ag-quarantine border-ag-quarantine'
                        : 'text-zinc-500 border-zinc-600'
                    }`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {bridgeHealth.quarantined} Quarantined
                  </Badge>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Section 2: Variant Cards */}
      <div className="flex items-center justify-between mb-1 px-1">
        <span className="text-xs text-zinc-500">Governance validation results by variant</span>
        <span className="text-xs text-zinc-600">Last tested: 2026-04-28</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {variantNames.map((name) => {
          const data = VARIANT_DATA[name];
          if (!data) return null;
          return <VariantCard key={name} name={name} data={data} isActive={activeVariants.includes(name)} />;
        })}
      </div>

      {/* Section 3: Latest Test Results */}
      <Card className="bg-gray-900 border-[#3A352F]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            Latest Swarm Interactions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-500">
                  <th className="text-left px-4 py-2 font-medium">Persona</th>
                  <th className="text-left px-4 py-2 font-medium">Variant</th>
                  <th className="text-left px-4 py-2 font-medium">Question</th>
                  <th className="text-center px-4 py-2 font-medium">Verdict</th>
                  <th className="text-right px-4 py-2 font-medium">Relevance</th>
                </tr>
              </thead>
              <tbody>
                {RECENT_RESULTS.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                    <td className="px-4 py-2 text-zinc-300 whitespace-nowrap">{r.persona_name}</td>
                    <td className="px-4 py-2 text-zinc-400 whitespace-nowrap">
                      {VARIANT_META[r.claw_variant]?.icon} {r.claw_variant}
                    </td>
                    <td className="px-4 py-2 text-zinc-500 max-w-xs truncate">
                      {r.input.length > 80 ? r.input.slice(0, 80) + '...' : r.input}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          r.verdict === 'HELD'
                            ? 'text-ag-active border-ag-active'
                            : 'text-ag-critical border-ag-critical'
                        }`}
                      >
                        {r.verdict === 'HELD' ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <XCircle className="w-3 h-3 mr-1" />
                        )}
                        {r.verdict === 'HELD' ? 'PASS' : 'FAIL'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="font-mono text-cyan-400">
                        {(r.governance_relevance * 100).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
